import { useEffect, useState } from 'react';
import CallHistoryModal from './CallHistoryModal';
import { getCallStatus, groupByCustomer, getKnownAgents } from './callUtils';
import { useAuth, isRefreshFor } from './AuthContext';
import LiveCallBadge, { indexLiveCalls, clockOffsetFrom } from './LiveCallBadge';
import { PhoneIcon, ChevronRightIcon } from './Icons';

// Every call in progress right now. Two sources:
// - Tata's own live view (GET /api/ringing-calls `live`, polled by the server
//   from Tata, see server/tataLiveCalls.js): ringing, in the IVR, or on a
//   call with an agent, for inbound and click-to-call alike. This is what
//   shows who picked up and who's talking, while the call is still going.
// - A "Call received on Server" webhook row with no outcome yet (getCallStatus
//   'Ringing'), for a number Tata's live view doesn't list.
// Ticks its own clock so a webhook card ages out on screen the same moment
// getCallStatus would stop calling it "Ringing" (RINGING_WINDOW_MS).
function LiveCalls({ calls, refreshCalls }) {
  const { authFetch, subscribeRefresh } = useAuth();
  const [now, setNow] = useState(() => Date.now());
  const [historyNumber, setHistoryNumber] = useState(null);
  const [liveCalls, setLiveCalls] = useState(() => new Map());
  const [clockOffsetMs, setClockOffsetMs] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // App.js already syncs `calls` on the SSE "calls" push and a 60s fallback
  // (refreshCalls only fetches calls changed since the last sync, so this
  // fast poll is cheap), but this tab is the most time-sensitive place that matters
  // — a call must appear the moment it starts ringing and vanish the moment
  // it's answered/missed. Some networks silently buffer/drop the long-lived
  // SSE connection (see AuthContext.js), so poll fast here too while this
  // tab is actually open, same fallback pattern IncomingCalls uses for its
  // ringing/viewing badges.
  useEffect(() => {
    if (!refreshCalls) return undefined;
    refreshCalls();
    const interval = setInterval(refreshCalls, 3000);
    return () => clearInterval(interval);
  }, [refreshCalls]);

  // Tata's live list: re-read on every "ringing" refresh (the server sends
  // one whenever a call starts, changes state or ends) and every 3s as the
  // same safety net.
  useEffect(() => {
    const fetchLive = async () => {
      try {
        const response = await authFetch('/api/ringing-calls');
        const data = await response.json();
        setLiveCalls(indexLiveCalls(data.live));
        setClockOffsetMs(clockOffsetFrom(data.serverTime));
      } catch (error) {
        console.error('Error fetching live calls:', error);
      }
    };
    fetchLive();
    const unsubscribe = subscribeRefresh((event) => {
      if (isRefreshFor(event, 'ringing', 'calls')) fetchLive();
    });
    const interval = setInterval(fetchLive, 3000);
    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [authFetch, subscribeRefresh]);

  const groupedCalls = groupByCustomer(calls);
  const groupByNumber = new Map(groupedCalls.map((group) => [group.customerNumber, group]));
  const ringingGroups = groupedCalls
    .filter((group) => !liveCalls.has(group.customerNumber))
    .filter((group) => getCallStatus(group.latestCall, now) === 'Ringing')
    .sort((a, b) => (b.latestCall.start_stamp || '').localeCompare(a.latestCall.start_stamp || ''));

  // Talking first, then the longest-running.
  const liveList = [...liveCalls.values()].sort(
    (a, b) =>
      (b.phase === 'connected') - (a.phase === 'connected') ||
      (a.startedAt || '').localeCompare(b.startedAt || '')
  );
  const onCallCount = liveList.filter((call) => call.phase === 'connected').length;
  const ringingCount = liveList.length - onCallCount + ringingGroups.length;

  const ringingFor = (call) => {
    const startedAt = new Date((call.start_stamp || '').replace(' ', 'T')).getTime();
    if (Number.isNaN(startedAt)) return '-';
    const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));
    const minutes = Math.floor(elapsed / 60);
    const seconds = String(elapsed % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const historyGroup = historyNumber ? groupByNumber.get(historyNumber) : null;

  return (
    <>
      <div className="stat-cards">
        <div className="stat-card stat-card-static active">
          <div className="stat-value">{onCallCount}</div>
          <div className="stat-label">On Call Now</div>
        </div>
        <div className="stat-card stat-card-static">
          <div className="stat-value">{ringingCount}</div>
          <div className="stat-label">Ringing Now</div>
        </div>
      </div>
      <div className="missed-card-list">
        {liveList.length === 0 && ringingGroups.length === 0 ? (
          <p className="agent-page-empty">No calls in progress right now.</p>
        ) : (
          <>
            {liveList.map((live) => {
              const hasHistory = groupByNumber.has(live.customerNumber);
              return (
                <button
                  key={live.callId}
                  type="button"
                  className="missed-card row-ringing"
                  onClick={() => hasHistory && setHistoryNumber(live.customerNumber)}
                  disabled={!hasHistory}
                  title={hasHistory ? 'Open customer profile' : 'First call from this number'}
                >
                  <span className="missed-card-avatar">
                    <PhoneIcon width={16} height={16} />
                  </span>
                  <span className="missed-card-body">
                    <span className="missed-card-top">
                      <span className="missed-card-number">{live.customerNumber}</span>
                      <span className="missed-card-time">
                        {live.direction === 'clicktocall' ? 'Click-to-call' : 'Incoming'}
                      </span>
                    </span>
                    <span className="missed-card-bottom">
                      <LiveCallBadge call={live} clockOffsetMs={clockOffsetMs} />
                      {!hasHistory && <span className="call-count">First call</span>}
                    </span>
                  </span>
                  {hasHistory && <ChevronRightIcon className="missed-card-chevron" width={18} height={18} />}
                </button>
              );
            })}
            {ringingGroups.map((group) => {
              const call = group.latestCall;
              return (
                <button
                  key={group.customerNumber}
                  type="button"
                  className="missed-card row-ringing"
                  onClick={() => setHistoryNumber(group.customerNumber)}
                  title="Open customer profile"
                >
                  <span className="missed-card-avatar">
                    <PhoneIcon width={16} height={16} />
                  </span>
                  <span className="missed-card-body">
                    <span className="missed-card-top">
                      <span className="missed-card-number">{group.customerNumber}</span>
                      <span className="missed-card-time">Calling {call.call_to_number || '-'}</span>
                    </span>
                    <span className="missed-card-bottom">
                      <span className="status ringing">Ringing · {ringingFor(call)}</span>
                      {call.billing_circle && <span className="call-count">{call.billing_circle}</span>}
                    </span>
                  </span>
                  <ChevronRightIcon className="missed-card-chevron" width={18} height={18} />
                </button>
              );
            })}
          </>
        )}
      </div>
      {historyGroup && (
        <CallHistoryModal
          group={historyGroup}
          onClose={() => setHistoryNumber(null)}
          agents={getKnownAgents(calls)}
          onCallTriggered={() => {}}
          liveCall={liveCalls.get(historyNumber)}
          clockOffsetMs={clockOffsetMs}
        />
      )}
    </>
  );
}

export default LiveCalls;
